import React, { useEffect, useState } from 'react';
import ReactPlayer from 'react-player';
import { Play, Pause, Volume2, VolumeX, Maximize, Loader } from 'lucide-react';

interface VideoPlayerProps {
  url: string;
  className?: string;
  /** YouTube/Vimeo: light preview. Direkta filer: startar i klick-för-att-spela (som DJ-videor). */
  light?: boolean;
  /** Om true monteras fil-video direkt (t.ex. admin-förhandsvisning). */
  immediate?: boolean;
}

function isHostedEmbed(url: string): boolean {
  return /youtube\.com|youtu\.be|vimeo\.com/i.test(url);
}

function mimeFromUrl(url: string): string | undefined {
  const path = url.split('?')[0].toLowerCase();
  if (path.endsWith('.mp4')) return 'video/mp4';
  if (path.endsWith('.webm')) return 'video/webm';
  if (path.endsWith('.mov')) return 'video/quicktime';
  return undefined;
}

/** Samma mönster som DjProfilePublicVideos LazyProfileVideo — poster/preview + klick innan <video> med controls. */
function LazyFileVideo({ url, className }: { url: string; className: string }) {
  const [activated, setActivated] = useState(false);
  const mime = mimeFromUrl(url);
  const previewSrc = `${url}#t=0.1`;

  if (activated) {
    return (
      <div className={`relative overflow-hidden rounded-none bg-black ${className}`}>
        <video
          key={url}
          className="h-full w-full object-contain bg-black"
          controls
          playsInline
          preload="metadata"
          src={url}
        >
          {mime ? <source src={url} type={mime} /> : null}
        </video>
      </div>
    );
  }

  return (
    <div className={`relative overflow-hidden rounded-none bg-slate-800 ${className}`}>
      <button
        type="button"
        onClick={() => setActivated(true)}
        className="relative flex h-full w-full items-center justify-center text-white/90 transition-colors hover:bg-slate-700/60"
        aria-label="Spela video"
      >
        <video
          className="pointer-events-none absolute inset-0 h-full w-full object-contain bg-black"
          muted
          playsInline
          preload="metadata"
          src={previewSrc}
          aria-hidden="true"
        />
        <span className="relative inline-flex items-center gap-2 rounded-full bg-black/55 px-4 py-2 text-sm font-medium">
          <Play className="h-4 w-4 fill-current" />
          Spela video
        </span>
      </button>
    </div>
  );
}

const VideoPlayer: React.FC<VideoPlayerProps> = ({
  url,
  className = '',
  light = false,
  immediate = false,
}) => {
  const [playing, setPlaying] = useState(false);
  const [volume, setVolume] = useState(0.8);
  const [muted, setMuted] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(false);
  const embed = isHostedEmbed(url);
  const mediaUrl = url;

  useEffect(() => {
    setPlaying(false);
    setError(false);
    setLoaded(Boolean(light && embed));
  }, [url, light, embed]);

  if (!embed && !immediate) {
    return <LazyFileVideo url={mediaUrl} className={className} />;
  }

  if (!embed && immediate) {
    return (
      <div className={`relative overflow-hidden rounded-none bg-black ${className}`}>
        <video
          key={mediaUrl}
          className="h-full w-full object-contain bg-black"
          controls
          playsInline
          preload="metadata"
          src={mediaUrl}
          onError={() => setError(true)}
        >
          {mimeFromUrl(mediaUrl) ? <source src={mediaUrl} type={mimeFromUrl(mediaUrl)} /> : null}
        </video>
        {error && (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-gray-900">
            <div className="max-w-sm px-4 text-center text-white">
              <p className="mb-2">Det gick inte att spela upp videon i webbläsaren</p>
              <button
                type="button"
                onClick={() => window.open(mediaUrl, '_blank', 'noopener,noreferrer')}
                className="rounded-none bg-purple-600 px-4 py-2 text-white hover:bg-purple-700"
              >
                Öppna i ny flik
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }

  const handlePlayPause = () => {
    setPlaying((p) => !p);
  };

  const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setVolume(parseFloat(e.target.value));
  };

  const handleToggleMute = () => {
    setMuted((m) => !m);
  };

  const handleReady = () => {
    setLoaded(true);
    setError(false);
  };

  const handleError = () => {
    setError(true);
    setLoaded(true);
  };

  return (
    <div className={`relative overflow-hidden rounded-none bg-black ${className}`}>
      {!loaded && !error && (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-gray-900">
          <Loader className="h-10 w-10 animate-spin text-white" />
        </div>
      )}

      {error && (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-gray-900">
          <div className="max-w-sm px-4 text-center text-white">
            <p className="mb-2">Det gick inte att spela upp videon i webbläsaren</p>
            <p className="mb-3 text-sm text-gray-300">
              Vanlig orsak: filen är QuickTime (.mov) eller HEVC. Öppna i ny flik eller be admin ladda upp MP4 (H.264).
            </p>
            <button
              type="button"
              onClick={() => window.open(mediaUrl, '_blank', 'noopener,noreferrer')}
              className="rounded-none bg-purple-600 px-4 py-2 text-white hover:bg-purple-700"
            >
              Öppna i ny flik
            </button>
          </div>
        </div>
      )}

      <ReactPlayer
        url={mediaUrl}
        width="100%"
        height="100%"
        playing={playing}
        volume={volume}
        muted={muted}
        controls={false}
        light={light}
        onReady={handleReady}
        onError={handleError}
        onClickPreview={() => {
          setLoaded(true);
          setPlaying(true);
        }}
      />

      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-4">
        <div className="flex items-center justify-between">
          <button
            type="button"
            onClick={handlePlayPause}
            className="text-white transition-colors hover:text-purple-300"
          >
            {playing ? <Pause size={24} /> : <Play size={24} />}
          </button>

          <div className="flex items-center">
            <button
              type="button"
              onClick={handleToggleMute}
              className="mr-2 text-white transition-colors hover:text-purple-300"
            >
              {muted ? <VolumeX size={20} /> : <Volume2 size={20} />}
            </button>

            <input
              type="range"
              min={0}
              max={1}
              step={0.1}
              value={volume}
              onChange={handleVolumeChange}
              className="w-20 accent-purple-600"
            />

            <button
              type="button"
              onClick={() => window.open(mediaUrl, '_blank', 'noopener,noreferrer')}
              className="ml-4 text-white transition-colors hover:text-purple-300"
            >
              <Maximize size={20} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VideoPlayer;
